import { forwardRef, type MouseEvent, type ReactNode, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown, Spinner } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useAuthStore } from "src/store/authStore";
import { useLogout } from "src/hooks/useAuth";
import { getDashboardsApi, setDashboardDefaultApi } from "src/api/dashboardApi";
import { DashboardListResponse } from "src/types/dashboard";
import useBuilderStore from "src/store/builderStore";
import useDashboardStore from "src/store/dashboardStore";
// import { getReportsApi } from "src/api/reportApi";
// import type { HeaderReportsResponse } from "src/types/header";
// import type { DashboardListResponse } from "src/types/dashboard";
// import { getDashboardsApi } from "src/api/dashboardApi";
// import useDashboardStore from "src/store/dashboardStore";



type ToggleButtonProps = {
  children: ReactNode;
  className?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

const UserMenuToggle = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  ({ children, className, onClick }, ref) => (
    <button
      ref={ref}
      type="button"
      className={className}
      onClick={(event) => {
        event.preventDefault();
        onClick?.(event);
      }}
    >
      {children}
    </button>
  ),
);

UserMenuToggle.displayName = "UserMenuToggle";

export default function BuilderHeader() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const navigate = useNavigate();
  const { user: userData, refreshToken } = useAuthStore();
  const { selectedDashboardCode, setSelectedDashboardCode } = useBuilderStore();
  const { setDefaultDashboard } = useDashboardStore();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();

  const queryClient = useQueryClient();

  const {
    mutate: setDefault,
    isPending: isSettingDefault,
    variables: settingDefaultCode,
  } = useMutation({
    mutationFn: (code: string) => setDashboardDefaultApi(code),
    onMutate: async (code) => {
      await queryClient.cancelQueries({ queryKey: ["dashboards"] });
      const previous = queryClient.getQueryData<DashboardListResponse>(["dashboards"]);
      queryClient.setQueryData<DashboardListResponse>(["dashboards"], (old) => {
        if (!old) return old;
        return {
          ...old,
          dashboards: old.dashboards.map((d) => ({
            ...d,
            is_default: d.code === code,
          })),
        };
      });
      setDefaultDashboard(code);
      return { previous };
    },
    onSuccess: () => {
      toast.success("Dashboard set as default.");
    },
    onError: (error: Error, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["dashboards"], context.previous);
        const prevDefault = context.previous.dashboards.find((d) => d.is_default);
        setDefaultDashboard(prevDefault?.code ?? "");
      }
      toast.error(error.message || "Failed to set dashboard as default.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });

  // const { data: allReports } = useQuery<HeaderReportsResponse, Error>({
  //   queryKey: ["reports"],
  //   queryFn: () => getReportsApi<HeaderReportsResponse>(),
  //   enabled: !!userData,
  //   // Never serve stale data — always hit the backend on every page/sort change
  //   staleTime: 0,
  //   // Remove from cache immediately so navigating back always refetches
  //   gcTime: 0,
  // });

  const { data: allDashboards } = useQuery<DashboardListResponse, Error>({
    queryKey: ["dashboards"],
    queryFn: () => getDashboardsApi(),
    enabled: !!userData,
  });

  useEffect(() => {
    const foundDashboard = allDashboards?.dashboards?.find(
      (dash) => dash.is_default === true,
    );

    if (foundDashboard && !selectedDashboardCode) {
      setSelectedDashboardCode(foundDashboard.code);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDashboards]);

  return (
    <nav className="navbar navbar-expand-lg auth-header">
      <div className="container-fluid container-auth px-5">
        <Link className="navbar-brand text-decoration-none" to={"/dashboards"}>
          Elusa Foresight
          <small className="d-block">Dashboard Builder</small>
        </Link>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#authHeader"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse ms-3" id="authHeader">
          <ul className="navbar-nav me-auto dashboard-tabs">
            {allDashboards?.dashboards?.map((dashboard) => {
              const isActive = selectedDashboardCode === dashboard?.code;
              return (
                <li key={dashboard?.code} className="nav-item">
                  <button
                    type="button"
                    className={`nav-link dashboard-tab${isActive ? " custom-active-link" : ""}`}
                    onClick={() => {
                      setSelectedDashboardCode(dashboard.code);
                      navigate(`/builder/${dashboard.code}`);
                    }}
                  >
                    {dashboard?.is_default ? (
                      <i className="bi bi-star-fill dashboard-default-star me-1" />
                    ) : (
                      <span
                        role="button"
                        className="set-default-btn me-1"
                        title="Set as default"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            isSettingDefault &&
                            settingDefaultCode === dashboard?.code
                          )
                            return;
                          setDefault(dashboard.code);
                        }}
                      >
                        {isSettingDefault &&
                        settingDefaultCode === dashboard?.code ? (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          <i className="bi bi-star" />
                        )}
                      </span>
                    )}
                    <span className="dashboard-tab__label">
                      {dashboard?.name}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <ul className="navbar-nav justify-content-end">
            {!userData ? (
              <div className="placeholder-glow">
                <span
                  className="placeholder"
                  style={{ width: "160px", height: "30px" }}
                ></span>
              </div>
            ) : (
              <Dropdown
                as="li"
                className="nav-item"
                show={isUserMenuOpen}
                onToggle={(nextShow) => setIsUserMenuOpen(nextShow)}
              >
                <Dropdown.Toggle
                  id="user-dropdown"
                  as={UserMenuToggle}
                  className={`nav-link ${
                    isUserMenuOpen ? "custom-active-link" : ""
                  }`}
                >
                  {userData?.name}
                </Dropdown.Toggle>

                <Dropdown.Menu
                  popperConfig={{
                    modifiers: [
                      {
                        name: "offset",
                        options: { offset: [0, 12] },
                      },
                    ],
                  }}
                >
                  <Dropdown.Item 
                    onClick={() =>
                      navigate(
                        selectedDashboardCode
                          ? `/dashboards/${selectedDashboardCode}`
                          : "/dashboards",
                      )
                    }
                    as="button"
                  >
                    Our Panel
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    as="button"
                    onClick={() => logout(refreshToken || "")}
                    disabled={isLoggingOut}
                  >
                    {isLoggingOut ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-1"
                        />{" "}
                        Signing out…
                      </>
                    ) : (
                      "Logout"
                    )}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}
